import React, { useState } from "react";
import { DollarSign } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Records/updates ONE equipment line's own Quote / Invoice / PO reference and
 * marks it paid — a single Delivery Note can have several lines invoiced
 * under different quotes/invoices/POs (e.g. partial deliveries billed
 * separately), so this must stay scoped to the one line, never cascaded
 * across the whole DN. Pre-fills with whatever is already on the line.
 */
export function MarkPaidModal({ item, onClose, onDone }) {
  const [quoteNumber, setQuoteNumber] = useState(item.quote_number || "");
  const [purchaseOrder, setPurchaseOrder] = useState(item.purchase_order || "");
  const [invoiceNumber, setInvoiceNumber] = useState(item.invoice_number || "");
  const [invoiceAmount, setInvoiceAmount] = useState(item.invoice_amount ?? "");
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/job-items/${item.id}/payment`, {
        payment_status: "paid",
        quote_number: quoteNumber.trim() || null,
        purchase_order: purchaseOrder.trim() || null,
        invoice_number: invoiceNumber.trim() || null,
        invoice_amount: invoiceAmount === "" ? null : Number(invoiceAmount),
      });
      if (r.data.dispatch_required) {
        toast.warning("Payment recorded — but this item hasn't been dispatched/handed over yet, " +
          "so it stays under \"In Dispatch\" instead of closing. Dispatch it (mark \"Ready for " +
          "Collection\" in Close-out) and it will close automatically.", { duration: 8000 });
      } else {
        toast.success("Line marked paid — billing info saved for this item");
      }
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={DollarSign}
      title="Mark line paid"
      subtitle={`${item.description || ""} · ${item.serial_number ? "SN " + item.serial_number : item.issue_number ? "Issue " + item.issue_number : "—"} · DN ${item.delivery_note_number || ""}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="mark-paid-cancel">Cancel</button>
        <button className="btn gold" onClick={confirm} disabled={busy} data-testid="mark-paid-confirm">
          <DollarSign size={13} /> {busy ? "Saving…" : "Mark Paid"}
        </button>
      </>}>

      <p className="t-mut" style={{ fontSize: 12.5, marginBottom: 12 }}>
        These references are saved against THIS equipment line only — other lines on the same
        Delivery Note keep their own quote/invoice/PO untouched.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>Quote number</label>
          <input className="input mono" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)}
            data-testid="mark-paid-quote" />
        </div>
        <div>
          <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>PO number</label>
          <input className="input mono" value={purchaseOrder} onChange={(e) => setPurchaseOrder(e.target.value)}
            data-testid="mark-paid-po" />
        </div>
        <div>
          <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>Invoice number</label>
          <input className="input mono" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
            data-testid="mark-paid-invoice" />
        </div>
        <div>
          <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>Invoice amount</label>
          <input className="input mono" type="number" step="0.01" value={invoiceAmount}
            onChange={(e) => setInvoiceAmount(e.target.value)} data-testid="mark-paid-amount" />
        </div>
      </div>
    </Modal>
  );
}
